// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyAdmin } from "@/types";
import Icon from "@/UI/atoms/Icon";
import PasswordPopup from "@/UI/popups/PasswordPopup";
import PhoneInput from "@/UI/inputs/PhoneInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Types --------------------------------------------------------------------------------------
export type TyAdminFormDetails = {
    /** The row being edited, or `null` to create one. */
    admin: TyAdmin | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * One form for both halves of the list: creating a member of Lozicore's own team, and editing one.
 *
 * The difference is not cosmetic. **A create returns a generated password** — the only time it exists
 * outside a hash — so this form hands straight over to `PasswordPopup` on success, and an edit simply
 * closes. Splitting it into two popups would mean two copies of the same four fields and the same
 * validation, which is how they drift.
 *
 * `isActive` is deliberately absent. Suspending somebody is a decision with a consequence worth
 * confirming, so it lives on the row behind a confirmation rather than as a switch somebody can leave
 * flipped by accident while editing a phone number.
 */
export default function AdminFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { admin, onDone } = (details ?? { admin: null }) as TyAdminFormDetails;
    const addComp = usePopup((state) => state.addComp);
    const me = useAuth((state) => state.admin);
    const isEditing = !!admin;
    /** Editing yourself: the identity fields are yours, the access box is not. */
    const isMe = isEditing && admin!._id === me?._id;

    const [name, setName] = useState(admin?.name ?? "");
    const [phone, setPhone] = useState<string | undefined>(admin?.phone ?? "");
    const [email, setEmail] = useState(admin?.email ?? "");
    const [notes, setNotes] = useState(admin?.notes ?? "");
    const [isSuper, setIsSuper] = useState(!!admin?.isSuper);
    const [formError, setFormError] = useState<string | null>(null);

    const save = useFetch<{ admin: TyAdmin; password?: string }>(isEditing ? `/admins/${admin!._id}` : "/admins", {}, false);

    useEffect(() => {
        if (!save.succeededAt) return;
        onDone?.();
        onClose();

        // A created account's password is shown once, immediately, with nothing in the way of it.
        const password = save.data?.password;
        if (password) {
            addComp({
                name: "password",
                Component: PasswordPopup,
                anim: "scale",
                details: { name: save.data?.admin?.name ?? name, password },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const handleSubmit = () => {
        setFormError(null);
        if (!name.trim()) return setFormError(t.admins.nameRequired);

        // The country picker leaves the calling code in place, so an untouched field is "+966" alone.
        const cleanPhone = (phone ?? "").replace(/\s/g, "");
        if (cleanPhone.length < 8) return setFormError(t.auth.phoneRequired);

        save.invoke({
            method: isEditing ? "PATCH" : "POST",
            data: { name: name.trim(), phone: cleanPhone, email: email.trim() || null, notes: notes.trim() || null, isSuper },
        });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{isEditing ? t.admins.editTitle : t.admins.createTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <TextInput value={name} onChange={setName} label={t.admins.fields.name} disabled={save.loading} />
                <PhoneInput value={phone} onChange={setPhone} label={t.admins.fields.phone} disabled={save.loading} />
                <TextInput value={email} onChange={setEmail} label={t.admins.fields.email} type="email" dir="ltr" disabled={save.loading} />
                <TextInput value={notes} onChange={setNotes} label={t.admins.fields.notes} multiline rows={2} disabled={save.loading} />
                <p className="side-form__hint">{t.admins.notesHint}</p>

                {/* One yes/no with a consequence, so the consequence is printed under it. */}
                <label className="side-form__check">
                    <input type="checkbox" checked={isSuper} onChange={(event) => setIsSuper(event.target.checked)} disabled={save.loading || isMe} />
                    <span>
                        {t.admins.fields.isSuper}
                        <small>{isMe ? t.admins.selfSuperHint : t.admins.isSuperHint}</small>
                    </span>
                </label>

                {(formError || save.error) && <p className="side-form__error">{formError ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
